import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { appleMapsUrl, googleMapsUrl, telUrl, wazeUrl } from '@shared/maps';
import type { Place } from '@shared/types';
import { appendEnglish } from '@shared/ptEn';
import { Bilingual } from './Bilingual';
import { AppleMapsIcon, ChevronRightIcon, GlobeIcon, GoogleMapsIcon, PhoneIcon, WazeIcon } from './Icons';

type ActionMenuProps = {
  place: Place;
  open: boolean;
  onClose: () => void;
};

type ActionItem = {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
  external?: boolean;
};

export function ActionMenu({ place, open, onClose }: ActionMenuProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
    const t = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  if (!mounted && !open) return null;

  const actions: ActionItem[] = [];
  if (place.phone) {
    actions.push({
      key: 'call',
      label: `Call ${place.phone}`,
      href: telUrl(place.phone),
      icon: <PhoneIcon />,
    });
  }
  actions.push({
    key: 'gmaps',
    label: 'Google Maps',
    href: googleMapsUrl(place.address),
    icon: <GoogleMapsIcon />,
    external: true,
  });
  actions.push({
    key: 'waze',
    label: 'Waze',
    href: wazeUrl(place.lat, place.lng),
    icon: <WazeIcon />,
    external: true,
  });
  actions.push({
    key: 'apple',
    label: 'Apple Maps',
    href: appleMapsUrl(place.address),
    icon: <AppleMapsIcon />,
    external: true,
  });
  if (place.website) {
    actions.push({
      key: 'web',
      label: 'Website',
      href: place.website,
      icon: <GlobeIcon />,
      external: true,
    });
  }

  return createPortal(
    <div
      className={`action-menu ${open ? 'action-menu--open' : 'action-menu--closing'}`}
      role="dialog"
      aria-label={appendEnglish(`Actions for ${place.name}`, place.name)}
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="action-menu__sheet" onClick={(e) => e.stopPropagation()}>
        <div className="action-menu__grabber" aria-hidden />
        <div className="action-menu__header">
          <div className="action-menu__title">
            <Bilingual text={place.name} />
          </div>
          <div className="action-menu__subtitle">
            <Bilingual text={place.address} />
          </div>
        </div>
        <ul className="action-menu__list">
          {actions.map((a) => (
            <li key={a.key}>
              <a
                className="action-menu__item"
                href={a.href}
                target={a.external ? '_blank' : undefined}
                rel={a.external ? 'noreferrer' : undefined}
                onClick={onClose}
              >
                <span className="action-menu__icon">{a.icon}</span>
                <span className="action-menu__label">{a.label}</span>
                <ChevronRightIcon />
              </a>
            </li>
          ))}
        </ul>
        <button type="button" className="action-menu__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
